import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { SslExpiryPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/wordpress')({
  component: WordPressPage,
  head: () => ({
    meta: [
      { title: 'WordPress Uptime Monitoring — SSL & Cron Alerts' },
      { name: 'description', content: 'Monitor WordPress externally — no plugin needed. Downtime, DB error, SSL expiry and cron alerts with 30s checks.' },
      { property: 'og:title', content: 'WordPress Uptime Monitoring — Downtime & SSL | Uptrack' },
      { property: 'og:description', content: 'External WordPress monitoring — no plugin needed. Detect downtime, database errors, and SSL issues. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-wordpress.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/wordpress' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For WordPress', item: 'https://uptrack.app/for/wordpress' },
          ],
        }),
      },
    ],
  }),
})

function WordPressPage() {
  return (
    <FeaturePage
      eyebrow='For · WordPress sites'
      section='WordPress sites'
      title='Know when your WordPress site goes down.'
      intro='External monitoring — no plugin to install, no PHP overhead, no conflicts. Uptrack checks your WordPress site every 30 seconds and alerts you on downtime, database errors, and SSL issues.'
      featuresHeading='WordPress monitoring without the plugin'
      features={[
        { title: 'HTTP monitoring', desc: 'Check your WordPress site from outside — just like your visitors see it. No server access or plugin required.' },
        { title: 'Database error detection', desc: 'Keyword monitoring catches "Error establishing a database connection" — the most common WordPress failure — instantly.' },
        { title: 'SSL expiry alerts', desc: "Let's Encrypt certificates auto-renew, but sometimes they don't. Get alerted before your site shows security warnings." },
        { title: 'Cron monitoring', desc: 'WordPress relies on wp-cron for scheduled tasks. Monitor it to make sure posts publish and plugins update on time.' },
        { title: 'No plugin needed', desc: 'Plugins slow your site and can conflict. Uptrack monitors externally — nothing to install, nothing to maintain.' },
        { title: 'Status page included', desc: 'Share a public status page with your readers or clients. They see real uptime data instead of wondering.' },
      ]}
      why={{ heading: 'WordPress breaks silently', body: 'A plugin update breaks your site. Your SSL certificate fails to renew. Your database runs out of connections. These things happen without warning — and your visitors see error pages. Uptrack watches from the outside and alerts you the moment something goes wrong.' }}
      cta={{ heading: '30-second checks on every plan', body: 'Monitor your WordPress sites externally. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <SslExpiryPiece />
        </ProductFrame>
      }
    />
  )
}
